import React, { useState, useCallback } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './ScenarioParametersPanel.css';

const ScenarioParametersPanel = ({ assumptions, setAssumptions, property, defaultAssumptions }) => {
    const [showAdditional, setShowAdditional] = useState(false);

    const isModified = useCallback((field) => {
        if (!defaultAssumptions) return false;
        return assumptions[field] !== defaultAssumptions[field];
    }, [assumptions, defaultAssumptions]);

    const update = useCallback((field, value) => {
        setAssumptions(prev => ({ ...prev, [field]: value }));
    }, [setAssumptions]);

    const updateNum = useCallback((field, value) => {
        const num = value === '' ? 0 : parseFloat(value);
        setAssumptions(prev => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
    }, [setAssumptions]);

    const downPaymentAmount = (assumptions.purchasePrice || 0) * (assumptions.downPaymentPercent || 0) / 100;

    return (
        <div className="scenario-params-panel">
            {/* Purchase Details */}
            <section className="spp-section">
                <h3 className="spp-heading">Purchase Details</h3>
                <div className="spp-row-3">
                    <div className="spp-field">
                        <label>Offer Price</label>
                        <div className={`spp-input-wrap currency${isModified('purchasePrice') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.purchasePrice || ''}
                                onChange={e => updateNum('purchasePrice', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Improvements / Rehab</label>
                        <div className={`spp-input-wrap currency${isModified('improvements') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.improvements || ''}
                                onChange={e => updateNum('improvements', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Closing Costs</label>
                        <div className={`spp-input-wrap currency${isModified('closingCosts') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.closingCosts || ''}
                                onChange={e => updateNum('closingCosts', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Financing */}
            <section className="spp-section">
                <h3 className="spp-heading">Financing</h3>
                <div className="spp-field">
                    <label>Down Payment</label>
                    <div className="spp-btn-group">
                        {[20, 25].map(pct => (
                            <button
                                key={pct}
                                className={`spp-btn ${assumptions.downPaymentPercent === pct ? 'active' : ''}`}
                                onClick={() => update('downPaymentPercent', pct)}
                            >
                                {pct}%
                            </button>
                        ))}
                        <div className={`spp-inline-input${isModified('downPaymentPercent') ? ' modified' : ''}`}>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={assumptions.downPaymentPercent || ''}
                                onChange={e => updateNum('downPaymentPercent', e.target.value)}
                            />
                            <span>%</span>
                        </div>
                    </div>
                    <span className="spp-hint">{formatCurrency(downPaymentAmount)} down</span>
                </div>

                <div className="spp-row-2">
                    <div className="spp-field">
                        <label>Interest Rate</label>
                        <div className="spp-slider-row">
                            <input
                                type="range"
                                min="2"
                                max="12"
                                step="0.01"
                                value={assumptions.interestRate || 6.89}
                                onChange={e => updateNum('interestRate', e.target.value)}
                            />
                            <div className={`spp-inline-input narrow${isModified('interestRate') ? ' modified' : ''}`}>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={assumptions.interestRate || ''}
                                    onChange={e => updateNum('interestRate', e.target.value)}
                                />
                                <span>%</span>
                            </div>
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Loan Term</label>
                        <select
                            className={`spp-select${isModified('loanTerm') ? ' modified' : ''}`}
                            value={assumptions.loanTerm || 30}
                            onChange={e => updateNum('loanTerm', e.target.value)}
                        >
                            <option value={15}>15 years</option>
                            <option value={20}>20 years</option>
                            <option value={30}>30 years</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* Revenue */}
            <section className="spp-section">
                <h3 className="spp-heading">Revenue</h3>
                <div className="spp-row-2">
                    <div className="spp-field">
                        <label>Rent Per Unit (/mo)</label>
                        <div className={`spp-input-wrap currency${isModified('rentPerUnit') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.rentPerUnit || ''}
                                onChange={e => updateNum('rentPerUnit', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Number of Units</label>
                        <input
                            type="number"
                            className={`spp-input${isModified('numberOfUnits') ? ' modified' : ''}`}
                            min="1"
                            value={assumptions.numberOfUnits || 1}
                            onChange={e => updateNum('numberOfUnits', e.target.value)}
                        />
                    </div>
                </div>
                <div className="spp-row-2">
                    <div className="spp-field">
                        <label>Vacancy Rate</label>
                        <div className={`spp-inline-input${isModified('vacancyRate') ? ' modified' : ''}`}>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={assumptions.vacancyRate || ''}
                                onChange={e => updateNum('vacancyRate', e.target.value)}
                            />
                            <span>%</span>
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Annual Rent Increase</label>
                        <div className={`spp-inline-input${isModified('annualRentIncrease') ? ' modified' : ''}`}>
                            <input
                                type="number"
                                step="0.1"
                                value={assumptions.annualRentIncrease || ''}
                                onChange={e => updateNum('annualRentIncrease', e.target.value)}
                            />
                            <span>%</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Operating Expenses */}
            <section className="spp-section">
                <h3 className="spp-heading">Operating Expenses</h3>
                <div className="spp-row-2">
                    <div className="spp-field">
                        <label>Property Taxes (/yr)</label>
                        <div className={`spp-input-wrap currency${isModified('propertyTaxes') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.propertyTaxes || ''}
                                onChange={e => updateNum('propertyTaxes', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Insurance (/yr)</label>
                        <div className={`spp-input-wrap currency${isModified('insurance') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.insurance || ''}
                                onChange={e => updateNum('insurance', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="spp-row-2">
                    <div className="spp-field">
                        <label>Maintenance / CapEx (/yr)</label>
                        <div className={`spp-input-wrap currency${isModified('maintenanceCapex') ? ' modified' : ''}`}>
                            <span className="spp-prefix">$</span>
                            <input
                                type="number"
                                value={assumptions.maintenanceCapex || ''}
                                onChange={e => updateNum('maintenanceCapex', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Property Mgmt Fee</label>
                        <div className={`spp-inline-input${isModified('propertyManagementFee') ? ' modified' : ''}`}>
                            <input
                                type="number"
                                step="0.1"
                                value={assumptions.propertyManagementFee || ''}
                                onChange={e => updateNum('propertyManagementFee', e.target.value)}
                                disabled={assumptions.isDIY}
                                className={assumptions.isDIY ? 'disabled' : ''}
                            />
                            <span>%</span>
                        </div>
                        {assumptions.isDIY && <span className="spp-hint">Disabled (DIY)</span>}
                    </div>
                </div>
            </section>

            {/* Additional Expenses (collapsible) */}
            <section className="spp-section">
                <button
                    className="spp-collapse-toggle"
                    onClick={() => setShowAdditional(!showAdditional)}
                >
                    <span>Additional Expenses</span>
                    {showAdditional ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {showAdditional && (
                    <div className="spp-collapse-body">
                        <div className="spp-row-3">
                            <div className="spp-field">
                                <label>HOA (/mo/unit)</label>
                                <div className={`spp-input-wrap currency${isModified('hoa') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.hoa || ''}
                                        onChange={e => updateNum('hoa', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="spp-field">
                                <label>Sewer (/mo/unit)</label>
                                <div className={`spp-input-wrap currency${isModified('sewer') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.sewer || ''}
                                        onChange={e => updateNum('sewer', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="spp-field">
                                <label>Water (/mo/unit)</label>
                                <div className={`spp-input-wrap currency${isModified('water') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.water || ''}
                                        onChange={e => updateNum('water', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="spp-row-2">
                            <div className="spp-field">
                                <label>Lawn Care (/mo, 7mo)</label>
                                <div className={`spp-input-wrap currency${isModified('lawnCare') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.lawnCare || ''}
                                        onChange={e => updateNum('lawnCare', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="spp-field">
                                <label>Tenant Placement (/yr)</label>
                                <div className={`spp-input-wrap currency${isModified('tenantPlacementFee') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.tenantPlacementFee || ''}
                                        onChange={e => updateNum('tenantPlacementFee', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="spp-row-3">
                            <div className="spp-field">
                                <label>Lease Renewal (/yr)</label>
                                <div className={`spp-input-wrap currency${isModified('leaseRenewalFee') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.leaseRenewalFee || ''}
                                        onChange={e => updateNum('leaseRenewalFee', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="spp-field">
                                <label>Annual Tune-up (/yr)</label>
                                <div className={`spp-input-wrap currency${isModified('annualTuneupFee') ? ' modified' : ''}`}>
                                    <span className="spp-prefix">$</span>
                                    <input
                                        type="number"
                                        value={assumptions.annualTuneupFee || ''}
                                        onChange={e => updateNum('annualTuneupFee', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="spp-field">
                                <label>Expense Increase</label>
                                <div className={`spp-inline-input${isModified('annualExpenseIncrease') ? ' modified' : ''}`}>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={assumptions.annualExpenseIncrease || ''}
                                        onChange={e => updateNum('annualExpenseIncrease', e.target.value)}
                                    />
                                    <span>%/yr</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* Settings */}
            <section className="spp-section">
                <h3 className="spp-heading">Settings</h3>
                <div className="spp-row-2">
                    <div className="spp-field">
                        <label>Appreciation Rate</label>
                        <div className={`spp-inline-input${isModified('appreciationRate') ? ' modified' : ''}`}>
                            <input
                                type="number"
                                step="0.1"
                                value={assumptions.appreciationRate || ''}
                                onChange={e => updateNum('appreciationRate', e.target.value)}
                            />
                            <span>%/yr</span>
                        </div>
                    </div>
                    <div className="spp-field">
                        <label>Self-Manage (DIY)</label>
                        <label className={`spp-toggle${isModified('isDIY') ? ' modified' : ''}`}>
                            <input
                                type="checkbox"
                                checked={assumptions.isDIY || false}
                                onChange={e => update('isDIY', e.target.checked)}
                            />
                            <span className="spp-toggle-slider" />
                        </label>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default ScenarioParametersPanel;
